import { Button } from "@base/ui/Button/Button";
import { mdiInformationOutline } from "@mdi/js";
import Icon from "@mdi/react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { fetchFinalPaymentAmount } from "../api/request";
import { type ApplyFormInstance, isApplyFormValid } from "../model/useApplyForm";

type Props = {
  form: ApplyFormInstance;
  plan: string;
  isPaymentPending: boolean;
};

// "DAY_PASS" -> "Day Pass", "YEARLY" -> "Yearly"
const formatLabel = (value: string) =>
  value
    .split("_")
    .map((word) => word[0] + word.slice(1).toLowerCase())
    .join(" ");

export const PaymentSection = ({ form, plan, isPaymentPending }: Props) => {
  const { t } = useTranslation();

  const [planType, planUnit] = plan.split("-");

  const { data: finalPaymentAmount } = useQuery({
    queryKey: ["finalPaymentAmount", planType, planUnit],
    enabled: !!planType && !!planUnit,
    queryFn: () =>
      fetchFinalPaymentAmount({
        type: planType as "SILVER" | "VIRTUAL_ADDRESS" | "DAY_PASS",
        billingCycle: planUnit as "MONTHLY" | "YEARLY" | "DAILY",
      }),
  });

  return (
    <div>
      <p className="text-subtitle2">{t("apply.payment.title")}</p>
      <div className="border border-gray-300 rounded-4 p-4 mt-4">
        <p className="text-subtitle4">{t("apply.payment.amountTitle")}</p>

        <div className="mt-3 flex flex-col gap-3">
          {finalPaymentAmount ? (
            <>
              <div className="flex justify-between items-center">
                <span className="text-body4 text-gray-600">
                  {formatLabel(planType)} Plan - {formatLabel(planUnit)}
                </span>
                <span className="text-body4 text-gray-600">${finalPaymentAmount.original_amount}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-subtitle4">{t("apply.payment.total")}</span>
                <span className="text-subtitle4">${finalPaymentAmount.total_amount}</span>
              </div>
            </>
          ) : null}
        </div>

        <div className="border border-gray-300 rounded-4 px-4 py-3 mt-3 flex gap-2 bg-gray-10 items-start">
          <Icon path={mdiInformationOutline} size="16px" className="shrink-0" />
          <p className="text-caption1">{t("apply.payment.popupWarning")}</p>
        </div>

        <form.Subscribe
          selector={(state) => ({
            companyName: state.values.companyName,
            firstName: state.values.firstName,
            lastName: state.values.lastName,
            phoneNumber: state.values.phoneNumber,
            email: state.values.email,
            plan: state.values.plan,
            agreedToTerms: state.values.agreedToTerms,
          })}
        >
          {(values) => (
            <Button type="submit" className="w-full mt-6" disabled={!isApplyFormValid(values) || isPaymentPending}>
              {t("apply.payment.pay")}
            </Button>
          )}
        </form.Subscribe>
      </div>
    </div>
  );
};
